/**
 * Strings for Khanmigo.
 */
import {plural, t} from "@lingui/core/macro";

import type {ReadingStyle} from "@repo/guide-types";
import {getLanguages} from "@repo/i18n";

const strings = {
    chatDisabledFocusMode: (teacherName: string, time: string) =>
        t`Your teacher, ${teacherName}, has turned on Focus Mode until **${time}** so I'm not available to chat right now. Any of your teachers can end Focus Mode at any time.`,
    chatDisabledInterestsOff: (url: string) =>
        t`Looks like you have turned off Interests. To enable this activity, please turn on Interests in the [Khanmigo Settings](${url}).`,
    headphoneIconTooltip: (
        on: boolean,
        disabled: boolean,
        unavailable: boolean,
    ) => {
        if (unavailable) {
            return t`Khanmigo Voice is currently unavailable`;
        }

        if (disabled) {
            return t`Khanmigo Voice is disabled`;
        }
        return on ? t`Turn OFF Khanmigo Voice` : t`Turn ON Khanmigo Voice`;
    },
    microphoneIconTooltip: (on: boolean, disabled: boolean) => {
        if (disabled) {
            return t`Speech Recognition is disabled`;
        }
        return on
            ? t`Turn OFF Speech Recognition`
            : t`Turn ON Speech Recognition`;
    },
    respondingInNSeconds: (seconds: number) =>
        plural(seconds, {
            one: "Not delivered. Next attempt in #s",
            other: "Not delivered. Next attempt in #s",
        }),
    guideLanguageName: (languageCode: string) => {
        // Apply any desired overrides to language names
        switch (languageCode) {
            case "pt": {
                return t`Brazilian Portuguese`;
            }
            default: {
                return getLanguages().get(languageCode)?.languageName;
            }
        }
    },
};

export const aiBatteryIconTooltipText = (
    fraction: number,
    isHighTraffic: boolean,
): string => {
    const percentage = Math.round(fraction * 100);
    if (!isHighTraffic) {
        return t`AI Power - ${percentage}%`;
    }

    // Message limit is subject to change so we'll translate the tool tip text
    // using a variable for the message limit.
    const messageLimit = 30;
    return t`AI Power - ${percentage}%
Traffic - High
Message Limit - ${messageLimit}/hr`;
};

export const aiBatteryIconAriaLabel = (
    fraction: number,
    isHighTraffic: boolean,
): string => {
    const percentage = Math.round(fraction * 100);
    if (!isHighTraffic) {
        return t`AI Power - ${percentage}%`;
    }

    // Message limit is subject to change so we'll translate the tool tip text
    // using a variable for the message limit.
    const messageLimit = 30;
    return t`AI Power - ${percentage}%, Traffic - High, Message Limit - ${messageLimit} messages per hour`;
};

export const paginatedPopupPageLabelText = (
    currentPage: number,
    totalPageCount: number,
): string => t`${currentPage} of ${totalPageCount}`;

export const translatedContentLinkTitle = (
    contentKind: string,
    title: string,
): string => t`${contentKind}: ${title}`;

// Labels for the ReadingStyle dropdown.
export function labelForReadingStyleSelect(readingStyle: ReadingStyle): string {
    const readingStyles: Partial<Record<ReadingStyle, string>> = {
        LOW: t`Simple `,
        MEDIUM: t`Standard`,
        HIGH: t`Professorial`,
    };
    return t`${Object.keys(readingStyles).indexOf(readingStyle) + 1}. ${readingStyles[readingStyle]}`;
}

// Chat message text to indicate that the user's reading style/level has changed.
export function readingStyleChangeMessage(readingStyle: ReadingStyle): string {
    const readingStyles: Partial<Record<ReadingStyle, string>> = {
        LOW: t`Simple `,
        MEDIUM: t`Standard`,
        HIGH: t`Professorial`,
    };
    return t`Your reading style is now set to ${readingStyles[readingStyle]}. You can change this anytime in the Khanmigo Preferences.`;
}

// Chat message text to indicate that the user's preferred AI Guide language has changed.
export function preferredGuideLanguageChangeMessage(
    languageCode: string,
): string {
    return t`Your preferred language is now set to ${strings.guideLanguageName(languageCode)}. You can change this anytime in the Khanmigo Preferences.`;
}

// Labels for the Guide language setting dropdown.
export function labelForGuideLanguageSelect(
    languageCode: string,
    languageLocalName: string,
    isDefault?: boolean,
): string {
    return isDefault
        ? t`${languageLocalName} (default)`
        : `${languageLocalName} (${strings.guideLanguageName(languageCode)})`;
}

export default strings;
